import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
} from "@mui/material";

const RPP = 25;

const isSupplierArchivedForInstance = (supplier, instanceUniqueId) => {
  if (!supplier) return false;
  if (supplier.archived === true) return true;
  const archivedInstanceIds = Array.isArray(supplier.ArchivedInstanceUniqueIds)
    ? supplier.ArchivedInstanceUniqueIds.map(String)
    : [];
  return archivedInstanceIds.includes(String(instanceUniqueId || ""));
};

const tabToSource = (tab) => {
  if (tab === "all") return "asi-supplier";
  return "core-supplier";
};

const makeSupplierOptionKey = (supplier = {}) => {
  const id = supplier?.id ?? supplier?.Id;
  const source = supplier?.supplierSource || supplier?.SupplierSource || "";
  return source && id != null ? `${source}:${id}` : "";
};

const supplierMatchesSavedIdentity = (supplier, value, source) => {
  const supplierId = supplier?.id ?? supplier?.Id;
  const supplierSource = supplier?.supplierSource || supplier?.SupplierSource;
  return (
    String(supplierId || "") === String(value || "") &&
    String(supplierSource || "") === String(source || "")
  );
};

const getPinnedSupplierSource = (supplier, fallback) =>
  supplier?.legacyUnresolvedSource
    ? ""
    : (supplier?.supplierSource || supplier?.SupplierSource || fallback);

const mergePinnedSupplierOption = (options = [], pinned = null) => {
  if (!pinned?.id && !pinned?.Id) return options;
  const pinnedKey = makeSupplierOptionKey(pinned);
  const exists = options.some((opt) => makeSupplierOptionKey(opt) === pinnedKey);
  return exists ? options : [pinned, ...options];
};

const getSupplierSourceForTab = (tab) =>
  tab === "all" ? "asi-supplier" : "core-supplier";

const buildSupplierQuery = ({ q = "", tab = "all", market = "GLOBAL" }) => {
  const baseQuery = String(q || "").trim();
  if (tab === "all" && market === "CANALL") {
    return `${baseQuery} market_segment:CANALL`.trim();
  }
  return baseQuery;
};

const normalizeSupplier = (row, fallbackSourceOrTab = "core", preserveMissingSource = false) => {
  const src = row?.Supplier || row || {};
  const id = src.id ?? src.Id;
  const fallbackSource =
    fallbackSourceOrTab === "all" || fallbackSourceOrTab === "core"
      ? getSupplierSourceForTab(fallbackSourceOrTab)
      : fallbackSourceOrTab;

  const supplierSource =
    src.supplierSource ||
    src.SupplierSource ||
    (preserveMissingSource ? "" : (fallbackSource || "core-supplier"));

  return {
    ...src,
    id,
    Id: src.Id ?? src.id,
    supplierSource,
    SupplierSource: supplierSource,
    supplierKey: id != null ? (supplierSource ? `${supplierSource}:${id}` : String(id)) : "",
    Name: src.Name ?? src.name ?? src.SupplierName ?? src.businessName ?? "",
    AsiNumber: src.AsiNumber ?? src.asiNumber ?? "",
    Email: src.Email ?? src.email ?? "",
    Phone: src.Phone ?? src.phone ?? "",
    Websites: src.Websites ?? src.websites ?? [],
    qbo_ref_id: src.qbo_ref_id,
    accountingRef: src.accountingRef,
    qbo_link_status: src.qbo_link_status,
  };
};

export default function SupplierSearchSelectAsync({
  instanceUniqueId,
  value,
  onChange,
  onSelectSupplier,
  selectedSupplierOption,
  label = "Supplier",
  showLabel = true,
  inputId,
  error = false,
  helperText = "",
  tab = "all",
  searchMode,
  market = "GLOBAL",
  placeholder = "Search suppliers...",
}) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const reqIdRef = useRef(0);

  const effectiveTab = searchMode || tab || "all";
  const expectedSourceForTab = tabToSource(effectiveTab);

  const pinnedSelectedSupplier = useMemo(() => {
    if (!selectedSupplierOption) return null;
    const normalized = normalizeSupplier(selectedSupplierOption, effectiveTab, true);
    if (isSupplierArchivedForInstance(normalized, instanceUniqueId)) return null;
    return normalized?.id != null ? normalized : null;
  }, [selectedSupplierOption, effectiveTab, instanceUniqueId]);

  useEffect(() => {
    if (!value || !pinnedSelectedSupplier) return;
    const expectedSource = getPinnedSupplierSource(pinnedSelectedSupplier, expectedSourceForTab);
    if (!supplierMatchesSavedIdentity(pinnedSelectedSupplier, value, expectedSource)) return;
    setSelected(pinnedSelectedSupplier);
    setOptions((prev) => mergePinnedSupplierOption(prev, pinnedSelectedSupplier));
  }, [value, pinnedSelectedSupplier, expectedSourceForTab]);

  useEffect(() => {
    reqIdRef.current += 1;
    setOptions(pinnedSelectedSupplier ? [pinnedSelectedSupplier] : []);
    setPage(1);
    setHasNext(false);
    setSelected(null);
    setInputValue("");
  }, [effectiveTab, pinnedSelectedSupplier]);

  const fetchPage = useCallback(async ({ q, pageNum, append }) => {
    const myReqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const finalQuery = buildSupplierQuery({ q, tab: effectiveTab, market });
      const { data } = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/suppliers/get-suppliers`,
        {
          params: {
            instanceUniqueId,
            q: finalQuery,
            page: pageNum,
            rpp: RPP,
            sort: "DFLT",
            tab: effectiveTab,
          },
          headers: { accessToken: localStorage.getItem("accessToken") },
        }
      );
      if (myReqId !== reqIdRef.current) return;

      const rows = data?.rows || data?.suppliers || data?.Results || data?.data || [];
      const normalized = rows
        .map((row) => normalizeSupplier(row, effectiveTab))
        .filter((supplier) =>
          supplier?.id != null &&
          !isSupplierArchivedForInstance(supplier, instanceUniqueId)
        );
      setOptions((prev) => {
        const next = append ? [...prev, ...normalized] : normalized;
        return mergePinnedSupplierOption(next, pinnedSelectedSupplier);
      });
      setPage(data?.page || pageNum);

      const next = typeof data?.hasNext === "boolean"
        ? data.hasNext
        : ((Number(data?.page || pageNum) * Number(data?.pageSize || RPP)) < Number(data?.total || 0));
      setHasNext(Boolean(next));
    } catch (e) {
      if (myReqId !== reqIdRef.current) return;
      console.error("Supplier search failed:", e);
      if (!append) setOptions([]);
      setHasNext(false);
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [effectiveTab, instanceUniqueId, market, pinnedSelectedSupplier]);

  useEffect(() => {
    setPage(1);
    setHasNext(false);
  }, [market]);

  useEffect(() => {
    if (!open) return;
    const handle = setTimeout(() => {
      fetchPage({ q: inputValue, pageNum: 1, append: false });
    }, 350);
    return () => clearTimeout(handle);
  }, [open, inputValue, fetchPage]);

  useEffect(() => {
    const normalizedValue = value ? String(value) : "";
    if (!normalizedValue) {
      setSelected(null);
      return;
    }

    if (
      pinnedSelectedSupplier &&
      supplierMatchesSavedIdentity(
        pinnedSelectedSupplier,
        value,
        getPinnedSupplierSource(pinnedSelectedSupplier, expectedSourceForTab)
      )
    ) {
      setSelected(pinnedSelectedSupplier);
      setOptions((prev) => mergePinnedSupplierOption(prev, pinnedSelectedSupplier));
      return;
    }

    const existing = options.find((opt) =>
      supplierMatchesSavedIdentity(
        opt,
        normalizedValue,
        opt?.supplierSource || opt?.SupplierSource || expectedSourceForTab
      )
    ) || options.find((opt) => String(opt.id) === normalizedValue);
    if (existing) {
      setSelected(existing);
      return;
    }

    let ignore = false;
    const fetchSelected = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/suppliers/${value}`,
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        );
        if (ignore) return;
        const supplier = normalizeSupplier(data?.supplier || data, effectiveTab);
        if (isSupplierArchivedForInstance(supplier, instanceUniqueId)) {
          setSelected(null);
          setOptions((prev) => prev.filter((opt) => String(opt?.id) !== String(supplier?.id)));
          onChange?.(null);
          onSelectSupplier?.(null);
          return;
        }
        if (supplier?.id != null) {
          setOptions((prev) => prev.some((opt) => String(opt.id) === String(supplier.id)) ? prev : [supplier, ...prev]);
          setSelected(supplier);
          return;
        }
      } catch (e) {
        try {
          const { data } = await axios.get(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/suppliers/get-suppliers`,
            {
              params: {
                q: buildSupplierQuery({ q: normalizedValue, tab: effectiveTab, market }),
                page: 1,
                rpp: 50,
                tab: effectiveTab,
                instanceUniqueId,
              },
              headers: { accessToken: localStorage.getItem("accessToken") },
            }
          );
          if (ignore) return;
          const rows = (data?.rows || data?.suppliers || data?.Results || data?.data || [])
            .map((row) => normalizeSupplier(row, effectiveTab))
            .filter((supplier) => !isSupplierArchivedForInstance(supplier, instanceUniqueId));
          const found = rows.find((row) => String(row?.id) === normalizedValue);
          if (found) {
            setOptions((prev) => prev.some((opt) => String(opt.id) === String(found.id)) ? prev : [found, ...prev]);
            setSelected(found);
          }
        } catch (fallbackErr) {
          console.error("Failed to load supplier", fallbackErr);
        }
      }
    };

    fetchSelected();
    return () => { ignore = true; };
  }, [value, instanceUniqueId, effectiveTab, market, pinnedSelectedSupplier, expectedSourceForTab]);

  const handleChange = (_, opt) => {
    if (isSupplierArchivedForInstance(opt, instanceUniqueId)) return;
    setSelected(opt || null);
    onChange?.(opt ? opt.id : null);
    onSelectSupplier?.(opt || null);
  };

  const listboxProps = {
    onScroll: (e) => {
      const el = e.currentTarget;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
      if (nearBottom && hasNext && !loading) {
        fetchPage({ q: inputValue, pageNum: page + 1, append: true });
      }
    },
    style: { maxHeight: 360, overflow: "auto" },
  };

  const valueObj = useMemo(() => (selected ? selected : null), [selected]);

  return (
    <FormControl fullWidth error={!!error}>
      <Autocomplete
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={options}
        value={valueObj}
        onChange={handleChange}
        onInputChange={(_, val, reason) => {
          if (reason === "input") setInputValue(val);
        }}
        loading={loading}
        ListboxProps={listboxProps}
        getOptionLabel={(s) => s?.Name || ""}
        isOptionEqualToValue={(opt, val) => {
          const optKey = opt?.supplierKey || `${opt?.supplierSource || ""}:${opt?.id || opt?.Id || ""}`;
          const valKey = val?.supplierKey || `${val?.supplierSource || ""}:${val?.id || val?.Id || ""}`;
          if (optKey && valKey) return optKey === valKey;
          return String(opt?.id || opt?.Id || "") === String(val?.id || val?.Id || "");
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            id={inputId}
            label={showLabel ? label : undefined}
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            placeholder={placeholder}
          />
        )}
        renderOption={(props, supplier) => {
          const statusText = (supplier.qbo_ref_id || supplier.accountingRef)
            ? "QBO linked"
            : "QBO unmapped";
          const supplierType = supplier.connected === true
            ? "Supplier"
            : (effectiveTab === "internal" ? "CustomSupplier" : (supplier.AsiNumber ? "Supplier" : "CustomSupplier"));
          const info = [
            supplier.AsiNumber ? `ASI #${supplier.AsiNumber}` : null,
            supplier.Email || null,
            statusText,
            supplierType,
          ].filter(Boolean).join(" • ");

          return (
            <li {...props} key={`supplier-opt-${supplier.id}`}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body2">{supplier.Name || "Unnamed supplier"}</Typography>
                <Typography variant="caption" color="text.secondary">{info}</Typography>
              </Box>
            </li>
          );
        }}
      />
      {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}
